import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionTitle } from '@/components/ui/stat-tile';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthStore } from '@/features/auth/auth.store';
import { GrowthBadge } from '@/features/dashboard/growth';
import {
  formatCalendarDate,
  formatDateTime,
  formatNumber,
  formatRelative,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import { MembersSheet } from './members-sheet';
import { SavedViews } from './saved-views';
import { subjectColor, useSubjectLabel } from './subject-labels';
import { SubjectBuilder, SubjectDot } from './subjects';
import {
  isEveryone,
  useUsageHeatmap,
  useUsagePages,
  useUsageSummary,
  type BreakdownDimension,
  type GroupSubject,
  type MemberList,
  type PageCountDto,
  type Subject,
  type SubjectPagesDto,
  type SubjectSummaryDto,
} from './usage-analytics.api';
import { UsageBreakdown } from './usage-breakdown';
import { csvShare, downloadCsv } from './usage-csv';
import { UsageFlows, type PageOption } from './usage-flows';
import {
  formatRate,
  formatShare,
  ratio,
  useDomainLabel,
  usePageLabel,
} from './usage-format';
import { DistinctivePages, PageMovers } from './usage-insights';
import { Empty, ErrorNotice, ExportButton, ShareCell } from './usage-parts';
import { UsageRetention } from './usage-retention';
import { UnusedPages } from './usage-unused';
import { UsageTrendChart } from './usage-trend-chart';
import { useUsageState } from './usage-url';

/**
 * How people use the client apps, one subject beside another.
 *
 * The customer tab's visit history answers "what did this caller do"; this
 * answers "how does this person, or this kind of person, use the app" — and
 * mostly by comparison. A list of a group's top pages looks much like anyone
 * else's; it is next to another subject that the differences show.
 *
 * Every figure that sits two subjects side by side is a rate rather than a
 * count — a share of that subject's own visits, visits per active user —
 * because one person and four thousand have no count in common worth putting
 * on the same axis.
 */

const ANY = 'any';

/** The apps offered in the filter. Named in `pageHistory.apps`; a new one is added in both places. */
const DOMAINS = ['admin-portal', 'webfront'];

/**
 * Pages asked for per subject — the most the API gives.
 *
 * The table shows a dozen, but the comparisons under it need the long tail: a
 * page a subject uses three times as much as everybody is rarely one of
 * anybody's top twelve.
 */
const TOP_PAGES = 100;
const PAGES_SHOWN = 12;

/** Pages offered in the "what comes after" picker. Past these the list is a tail nobody scrolls. */
const FLOW_PAGE_OPTIONS = 40;

const DIMENSIONS: BreakdownDimension[] = [
  'age',
  'gender',
  'language',
  'domain',
  'business',
];

/** Everybody, under the page's own filters: what the subjects are read against. */
const BASELINE: Subject[] = [{ kind: 'group', key: 'baseline', slot: 0 }];

/** Every day from start to end. The API leaves quiet days out; a trend line needs them back as zeroes. */
function daysBetween(start: string, end: string) {
  const days: string[] = [];
  if (!start || !end || start > end) return days;
  const cursor = new Date(`${start}T00:00:00.000Z`);
  const last = new Date(`${end}T00:00:00.000Z`);
  // Stepped in UTC so a clock change inside the range cannot skip or repeat a day.
  while (cursor <= last && days.length < 400) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

/** Percent change against the earlier span, or null where there is nothing to compare with. */
function change(summary: SubjectSummaryDto) {
  const before = summary.previous?.visits ?? 0;
  if (!summary.previous || before === 0) return null;
  return ((summary.totals.visits - before) / before) * 100;
}

type TrendMetric = 'perUser' | 'activeUsers' | 'visits';

function SummaryTable({
  subjects,
  summaries,
  busy,
  onOpenMembers,
}: {
  subjects: Subject[];
  summaries: Map<string, SubjectSummaryDto>;
  busy: boolean;
  onOpenMembers: (subject: GroupSubject) => void;
}) {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const canOpenCustomers = useAuthStore((s) => s.can('isCustomerService'));

  const exportCsv = () =>
    downloadCsv('usage_summary.csv', [
      [
        t('usage.subject'),
        t('usage.members'),
        t('usage.activeUsers'),
        t('usage.visits'),
        t('usage.previousVisits'),
        t('usage.sessions'),
        t('usage.visitsPerUser'),
        t('usage.pagesPerSession'),
        t('usage.lastSeenUtc'),
      ],
      ...subjects.flatMap((subject) => {
        const summary = summaries.get(subject.key);
        if (!summary) return [];
        const { totals } = summary;
        return [
          [
            label(subject),
            summary.members,
            totals.activeUsers,
            totals.visits,
            summary.previous?.visits ?? '',
            totals.sessions,
            Number(ratio(totals.visits, totals.activeUsers).toFixed(2)),
            totals.sessions > 0
              ? Number(ratio(totals.visits, totals.sessions).toFixed(2))
              : '',
            totals.lastVisitedOn ?? '',
          ],
        ];
      }),
    ]);

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl">
        <BusyOverlay show={busy} />
        <div
          className={cn(
            'bg-card overflow-x-auto rounded-xl border',
            busy && 'opacity-60 transition-opacity',
          )}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('usage.subject')}</TableHead>
                <TableHead className="text-right">
                  {t('usage.members')}
                </TableHead>
                <TableHead className="text-right">
                  {t('usage.activeUsers')}
                </TableHead>
                <TableHead className="text-right">
                  {t('usage.visits')}
                </TableHead>
                <TableHead className="text-right">
                  {t('usage.sessions')}
                </TableHead>
                <TableHead className="text-right">
                  {t('usage.visitsPerUser')}
                </TableHead>
                <TableHead className="text-right">
                  {t('usage.pagesPerSession')}
                </TableHead>
                <TableHead>{t('usage.lastSeen')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {subjects.map((subject) => {
                const summary = summaries.get(subject.key);
                const totals = summary?.totals;
                return (
                  <TableRow key={subject.key}>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <SubjectDot subject={subject} />
                        <span className="font-medium">{label(subject)}</span>
                      </span>
                    </TableCell>
                    {!summary || !totals ? (
                      <TableCell colSpan={8}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    ) : (
                      <>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(summary.members)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(totals.activeUsers)}
                          {/* Reach only means something for a group: for one
                            person it is always all or nothing. */}
                          {subject.kind === 'group' && summary.members > 0 && (
                            <span className="text-muted-foreground ml-1.5 text-xs">
                              {formatShare(
                                ratio(totals.activeUsers, summary.members),
                              )}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className="inline-flex items-center justify-end gap-1.5">
                            {formatNumber(totals.visits)}
                            <GrowthBadge change={change(summary)} />
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(totals.sessions)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatRate(ratio(totals.visits, totals.activeUsers))}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {totals.sessions > 0
                            ? formatRate(ratio(totals.visits, totals.sessions))
                            : '—'}
                        </TableCell>
                        <TableCell
                          className="text-muted-foreground whitespace-nowrap"
                          title={
                            totals.lastVisitedOn
                              ? formatDateTime(totals.lastVisitedOn)
                              : undefined
                          }
                        >
                          {totals.lastVisitedOn
                            ? formatRelative(totals.lastVisitedOn)
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {subject.kind === 'group' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={totals.activeUsers === 0}
                              onClick={() => onOpenMembers(subject)}
                            >
                              {t('usage.viewMembers')}
                            </Button>
                          ) : (
                            canOpenCustomers &&
                            subject.email && (
                              <Button variant="ghost" size="sm" asChild>
                                <Link
                                  to={`/customers?q=${encodeURIComponent(subject.email)}`}
                                >
                                  {t('usage.openCustomer')}
                                </Link>
                              </Button>
                            )
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      {summaries.size > 0 && <ExportButton onExport={exportCsv} />}
    </div>
  );
}

function TopPages({
  subjects,
  pages,
  busy,
}: {
  subjects: Subject[];
  pages: Map<string, SubjectPagesDto>;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const pageLabel = usePageLabel();
  const domainLabel = useDomainLabel();
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const byPage = new Map<
      string,
      {
        page: string;
        domain: string;
        cells: Map<string, PageCountDto>;
        peak: number;
      }
    >();

    for (const subject of subjects) {
      const total = pages.get(subject.key)?.totalVisits ?? 0;
      for (const entry of pages.get(subject.key)?.pages ?? []) {
        // Pages kept only because they used to be popular; the movers panel
        // wants them, a ranking of what is opened now does not.
        if (entry.visits === 0) continue;
        const id = `${entry.domain}|${entry.page}`;
        const row = byPage.get(id) ?? {
          page: entry.page,
          domain: entry.domain,
          cells: new Map<string, PageCountDto>(),
          peak: 0,
        };
        row.cells.set(subject.key, entry);
        row.peak = Math.max(row.peak, ratio(entry.visits, total));
        byPage.set(id, row);
      }
    }

    // Ranked by the largest share any subject gives the page, so a page that
    // matters a great deal to one of them is not buried under pages that
    // matter a little to all of them.
    return [...byPage.values()].sort((a, b) => b.peak - a.peak);
  }, [subjects, pages]);

  const scale = rows[0]?.peak ?? 0;
  const shown = showAll ? rows : rows.slice(0, PAGES_SHOWN);

  if (rows.length === 0) return <Empty>{t('usage.noPages')}</Empty>;

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl">
        <BusyOverlay show={busy} />
        <div
          className={cn(
            'bg-card overflow-x-auto rounded-xl border',
            busy && 'opacity-60 transition-opacity',
          )}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('usage.page')}</TableHead>
                {subjects.map((subject) => (
                  <TableHead key={subject.key} className="min-w-36">
                    <span className="flex items-center gap-2">
                      <SubjectDot subject={subject} />
                      <span className="truncate">{label(subject)}</span>
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row) => {
                return (
                  <TableRow key={`${row.domain}|${row.page}`}>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium">
                          {pageLabel(row.domain, row.page)}
                        </span>
                        <Badge
                          variant="outline"
                          className="text-[10px] font-normal"
                        >
                          {domainLabel(row.domain)}
                        </Badge>
                      </span>
                    </TableCell>
                    {subjects.map((subject) => {
                      const cell = row.cells.get(subject.key);
                      const share = cell
                        ? ratio(
                            cell.visits,
                            pages.get(subject.key)?.totalVisits ?? 0,
                          )
                        : 0;
                      return (
                        <TableCell
                          key={subject.key}
                          title={
                            cell
                              ? t('usage.pageCell', {
                                  visits: formatNumber(cell.visits),
                                  users: formatNumber(cell.users),
                                })
                              : undefined
                          }
                        >
                          {cell ? (
                            <ShareCell
                              share={share}
                              scale={scale}
                              color={subjectColor(subject)}
                            />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {rows.length > PAGES_SHOWN && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll
              ? t('usage.showFewer')
              : t('usage.showAllPages', { count: rows.length })}
          </Button>
        )}
        {/* Every row, not only the dozen on screen, and one line per page
            and subject: the wide layout above cannot be sorted or filtered
            once it is in a spreadsheet. */}
        <ExportButton
          onExport={() =>
            downloadCsv('usage_top_pages.csv', [
              [
                t('usage.page'),
                t('usage.pageKey'),
                t('usage.app'),
                t('usage.subject'),
                t('usage.visits'),
                t('usage.members'),
                t('usage.share'),
              ],
              ...rows.flatMap((row) =>
                subjects.flatMap((subject) => {
                  const cell = row.cells.get(subject.key);
                  if (!cell) return [];
                  return [
                    [
                      pageLabel(row.domain, row.page),
                      row.page,
                      row.domain,
                      label(subject),
                      cell.visits,
                      cell.users,
                      csvShare(
                        ratio(
                          cell.visits,
                          pages.get(subject.key)?.totalVisits ?? 0,
                        ),
                      ),
                    ],
                  ];
                }),
              ),
            ])
          }
        />
      </div>
    </div>
  );
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const WEEKDAYS = Array.from({ length: 7 }, (_, day) => day);

/** Short weekday names in the reader's language, Sunday first. */
function weekdayNames(language: string) {
  const format = new Intl.DateTimeFormat(language, {
    weekday: 'short',
    timeZone: 'UTC',
  });
  // 2023-01-01 was a Sunday.
  return WEEKDAYS.map((day) =>
    format.format(new Date(Date.UTC(2023, 0, 1 + day))),
  );
}

function HeatGrid({
  subject,
  cells,
}: {
  subject: Subject;
  cells: { dayOfWeek: number; hour: number; visits: number }[];
}) {
  const { t, i18n } = useTranslation();
  const label = useSubjectLabel();
  const names = useMemo(() => weekdayNames(i18n.language), [i18n.language]);

  const counts = useMemo(() => {
    const grid = new Map<string, number>();
    for (const cell of cells)
      grid.set(`${cell.dayOfWeek}-${cell.hour}`, cell.visits);
    return grid;
  }, [cells]);
  const max = Math.max(0, ...cells.map((cell) => cell.visits));

  return (
    <div className="bg-card space-y-2 rounded-xl border p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <SubjectDot subject={subject} />
          <span className="truncate text-sm font-medium">{label(subject)}</span>
        </span>
        {/* Each grid is shaded against its own busiest hour, and says so:
            the grids compare when, not how much. */}
        <span className="text-muted-foreground shrink-0 text-xs">
          {t('usage.heatPeak', { count: max, formatted: formatNumber(max) })}
        </span>
      </div>

      {max === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          {t('usage.noVisits')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[30rem] border-separate border-spacing-0.5">
            <thead>
              <tr>
                <th className="w-10" />
                {HOURS.map((hour) => (
                  <th
                    key={hour}
                    className="text-muted-foreground pb-0.5 text-[10px] font-normal"
                  >
                    {hour % 3 === 0 ? hour : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((day) => (
                <tr key={day}>
                  <th className="text-muted-foreground pr-1.5 text-right text-xs font-normal">
                    {names[day]}
                  </th>
                  {HOURS.map((hour) => {
                    const count = counts.get(`${day}-${hour}`) ?? 0;
                    return (
                      <td
                        key={hour}
                        title={`${names[day]} ${String(hour).padStart(2, '0')}:00 · ${formatNumber(count)}`}
                        className={cn(
                          'h-5 rounded-sm',
                          count === 0 && 'bg-muted',
                        )}
                        style={
                          count > 0
                            ? {
                                backgroundColor: subjectColor(subject),
                                // Zero stays empty rather than taking the
                                // floor of the scale, as on the sales heatmap.
                                opacity: 0.15 + (0.85 * count) / max,
                              }
                            : undefined
                        }
                      >
                        <span className="sr-only">
                          {names[day]} {hour}: {formatNumber(count)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function UsageAnalyticsPage() {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const domainLabel = useDomainLabel();

  const state = useUsageState();
  const { filters, subjects } = state;
  const { start, end } = filters;

  const [metric, setMetric] = useState<TrendMetric>('perUser');
  const [dimension, setDimension] = useState<BreakdownDimension>('age');
  const [membersOf, setMembersOf] = useState<{
    subject: GroupSubject;
    list: MemberList;
  } | null>(null);

  const summary = useUsageSummary(filters, subjects);
  const pages = useUsagePages(filters, subjects, TOP_PAGES);
  const heatmap = useUsageHeatmap(filters, subjects);

  // Asked for on its own, and only when somebody is there to be compared with
  // it: with nobody but "everyone" on the page it would be the same question
  // as the one above, sent twice.
  const comparing = subjects.some((subject) => !isEveryone(subject));
  const baseline = useUsagePages(filters, comparing ? BASELINE : [], TOP_PAGES);

  const fetching = summary.isFetching || pages.isFetching || heatmap.isFetching;
  const refetchAll = () => {
    void summary.refetch();
    void pages.refetch();
    void heatmap.refetch();
    if (comparing) void baseline.refetch();
  };

  // Keyed by subject, and only read for subjects still on the page: while a
  // new answer loads the held one may still carry a subject just removed.
  const summaries = useMemo(
    () => new Map((summary.data ?? []).map((entry) => [entry.key, entry])),
    [summary.data],
  );
  const pagesBySubject = useMemo(
    () => new Map((pages.data ?? []).map((entry) => [entry.key, entry])),
    [pages.data],
  );
  const heatBySubject = useMemo(
    () =>
      new Map((heatmap.data ?? []).map((entry) => [entry.key, entry.cells])),
    [heatmap.data],
  );

  // The pages to offer in the flows picker: whatever the subjects on the page
  // open most, taken together.
  const pageOptions = useMemo(() => {
    const visits = new Map<string, PageOption & { visits: number }>();
    for (const entry of pages.data ?? []) {
      for (const page of entry.pages) {
        const id = `${page.domain}|${page.page}`;
        const known = visits.get(id);
        if (known) known.visits += page.visits;
        else
          visits.set(id, {
            domain: page.domain,
            page: page.page,
            visits: page.visits,
          });
      }
    }
    return [...visits.values()]
      .filter((option) => option.visits > 0)
      .sort((a, b) => b.visits - a.visits)
      .slice(0, FLOW_PAGE_OPTIONS);
  }, [pages.data]);

  const days = useMemo(() => daysBetween(start, end), [start, end]);
  const series = useMemo(
    () =>
      subjects.map((subject) => {
        const byDay = new Map(
          (summaries.get(subject.key)?.days ?? []).map((day) => [
            day.date.slice(0, 10),
            day,
          ]),
        );
        return {
          key: subject.key,
          label: label(subject),
          color: subjectColor(subject),
          values: days.map((day) => {
            const entry = byDay.get(day);
            if (!entry) return 0;
            if (metric === 'visits') return entry.visits;
            if (metric === 'activeUsers') return entry.activeUsers;
            return ratio(entry.visits, entry.activeUsers);
          }),
        };
      }),
    // `label` is rebuilt every render and reads only the subjects and the
    // language, both of which are already what this recomputes on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjects, summaries, days, metric, t],
  );

  const rangeInvalid = start > end;

  const filterFields: FilterField[] = [
    {
      id: 'start',
      label: t('usage.from'),
      value: start,
      onChange: state.setStart,
      type: 'date',
      resetValue: state.defaultRange.start,
    },
    {
      id: 'end',
      label: t('usage.to'),
      value: end,
      onChange: state.setEnd,
      type: 'date',
      resetValue: state.defaultRange.end,
    },
    {
      id: 'domain',
      label: t('usage.app'),
      value: filters.domain ?? ANY,
      onChange: (value) => state.setDomain(value === ANY ? null : value),
      options: [
        { value: ANY, label: t('pageHistory.allApps') },
        // A link may name an app this build has no entry for; it still has
        // to be selectable, or the select shows nothing for the active filter.
        ...[
          ...new Set([...DOMAINS, ...(filters.domain ? [filters.domain] : [])]),
        ].map((slug) => ({ value: slug, label: domainLabel(slug) })),
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('usage.title')}
        description={t('usage.subtitle')}
        actions={
          <>
            <SavedViews search={state.search} onOpen={state.open} />
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={fetching || subjects.length === 0}
              onClick={refetchAll}
            >
              <RefreshCw className={fetching ? 'animate-spin' : ''} />
            </Button>
          </>
        }
      />

      <FilterBar
        busy={fetching}
        fields={filterFields}
        summary={
          <p className="text-muted-foreground text-sm">
            {formatCalendarDate(start)} – {formatCalendarDate(end)}
            {' · '}
            {t('usage.retention')}
          </p>
        }
      />

      <SubjectBuilder
        subjects={subjects}
        onChange={state.setSubjects}
        disabled={fetching}
      />

      {rangeInvalid && <Empty>{t('usage.rangeInvalid')}</Empty>}

      {!rangeInvalid && subjects.length === 0 && (
        <Empty>{t('usage.noSubjects')}</Empty>
      )}

      {!rangeInvalid && subjects.length > 0 && (
        <>
          {summary.isError && (
            <ErrorNotice
              message={summary.error?.message || t('usage.loadFailed')}
              onRetry={refetchAll}
            />
          )}

          {!summary.isError && (
            <>
              <section className="space-y-2">
                <SectionTitle
                  title={t('usage.summaryTitle')}
                  help={t('usage.summaryHelp')}
                />
                <SummaryTable
                  subjects={subjects}
                  summaries={summaries}
                  busy={summary.isFetching && !summary.isLoading}
                  onOpenMembers={(subject) =>
                    setMembersOf({ subject, list: 'members' })
                  }
                />
              </section>

              <section className="space-y-2">
                <SectionTitle
                  title={t('usage.trendTitle')}
                  help={t(`usage.trendHelp.${metric}`)}
                  actions={
                    <Select
                      value={metric}
                      onValueChange={(value) => setMetric(value as TrendMetric)}
                    >
                      <SelectTrigger
                        className="w-52"
                        aria-label={t('usage.trendMetric')}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="perUser">
                          {t('usage.visitsPerUser')}
                        </SelectItem>
                        <SelectItem value="activeUsers">
                          {t('usage.activeUsers')}
                        </SelectItem>
                        <SelectItem value="visits">
                          {t('usage.visits')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
                {summary.isLoading ? (
                  <Skeleton className="h-72 rounded-xl" />
                ) : (
                  <UsageTrendChart
                    days={days}
                    series={series}
                    format={metric === 'perUser' ? formatRate : formatNumber}
                    busy={summary.isFetching}
                  />
                )}
              </section>
            </>
          )}

          {pages.isError ? (
            <section className="space-y-2">
              <SectionTitle title={t('usage.pagesTitle')} />
              <ErrorNotice
                message={pages.error?.message || t('usage.loadFailed')}
                onRetry={() => void pages.refetch()}
              />
            </section>
          ) : (
            <>
              <section className="space-y-2">
                <SectionTitle
                  title={t('usage.pagesTitle')}
                  help={t('usage.pagesHelp')}
                />
                {pages.isLoading ? (
                  <Skeleton className="h-72 rounded-xl" />
                ) : (
                  <TopPages
                    subjects={subjects}
                    pages={pagesBySubject}
                    busy={pages.isFetching}
                  />
                )}
              </section>

              {comparing && !baseline.isError && (
                <section className="space-y-2">
                  <SectionTitle
                    title={t('usage.distinctTitle')}
                    help={t('usage.distinctHelp')}
                  />
                  {pages.isLoading || baseline.isLoading ? (
                    <Skeleton className="h-40 rounded-xl" />
                  ) : (
                    <DistinctivePages
                      subjects={subjects}
                      pages={pagesBySubject}
                      baseline={baseline.data?.[0]}
                      top={TOP_PAGES}
                    />
                  )}
                </section>
              )}

              <section className="space-y-2">
                <SectionTitle
                  title={t('usage.moversTitle')}
                  help={t('usage.moversHelp')}
                />
                {pages.isLoading ? (
                  <Skeleton className="h-40 rounded-xl" />
                ) : (
                  <PageMovers subjects={subjects} pages={pagesBySubject} />
                )}
              </section>
            </>
          )}

          <section className="space-y-2">
            <SectionTitle
              title={t('usage.breakdownTitle')}
              help={t('usage.breakdownHelp')}
              actions={
                <Select
                  value={dimension}
                  onValueChange={(value) =>
                    setDimension(value as BreakdownDimension)
                  }
                >
                  <SelectTrigger
                    className="w-44"
                    aria-label={t('usage.breakdownBy')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIMENSIONS.map((entry) => (
                      <SelectItem key={entry} value={entry}>
                        {t(`usage.dimensions.${entry}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
            />
            <UsageBreakdown
              filters={filters}
              subjects={subjects}
              dimension={dimension}
            />
          </section>

          <section className="space-y-2">
            <SectionTitle
              title={t('usage.heatTitle')}
              help={t('usage.heatHelp')}
            />
            {heatmap.isError ? (
              <ErrorNotice
                message={heatmap.error?.message || t('usage.loadFailed')}
                onRetry={() => void heatmap.refetch()}
              />
            ) : heatmap.isLoading ? (
              <Skeleton className="h-56 rounded-xl" />
            ) : (
              <div
                className={cn(
                  'grid gap-3 xl:grid-cols-2',
                  heatmap.isFetching && 'opacity-60 transition-opacity',
                )}
              >
                {subjects.map((subject) => (
                  <HeatGrid
                    key={subject.key}
                    subject={subject}
                    cells={heatBySubject.get(subject.key) ?? []}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <SectionTitle
              title={t('usage.flowsTitle')}
              help={t('usage.flowsHelp')}
            />
            <UsageFlows
              filters={filters}
              subjects={subjects}
              pageOptions={pageOptions}
            />
          </section>

          <section className="space-y-2">
            <SectionTitle
              title={t('usage.retentionTitle')}
              help={t('usage.retentionHelp')}
            />
            <UsageRetention
              filters={filters}
              subjects={subjects}
              onOpenLapsed={(subject) =>
                setMembersOf({ subject, list: 'lapsed' })
              }
            />
          </section>
        </>
      )}

      {/* Outside the subjects' block on purpose: it is about everybody's use
          of this portal and has an answer whoever is being compared. Hidden
          when the filter is on another app, whose pages are not known here. */}
      {!rangeInvalid &&
        (!filters.domain || filters.domain === 'admin-portal') && (
          <section className="space-y-2">
            <SectionTitle
              title={t('usage.unusedTitle')}
              help={t('usage.unusedHelp')}
            />
            <UnusedPages filters={filters} />
          </section>
        )}

      <MembersSheet
        filters={filters}
        subject={membersOf?.subject ?? null}
        list={membersOf?.list ?? 'members'}
        onClose={() => setMembersOf(null)}
      />
    </div>
  );
}
